import { useEffect, useMemo, useState } from 'react'
import {
  ArrowRight,
  Check,
  ExternalLink,
  FileText,
  Menu,
  MessageCircle,
  Megaphone,
  Search,
  Sparkles,
  Star,
  TrendingUp,
  X,
} from 'lucide-react'
import { articles, categories, products, type Product } from './data/products'
import { catalog, type CatalogProduct } from './data/catalog'

const iconMap = {
  trend: TrendingUp,
  sparkles: Sparkles,
  megaphone: Megaphone,
  check: Check,
  message: MessageCircle,
  file: FileText,
}

function Stars({ score }: { score: number }) {
  return (
    <span className="stars" aria-label={`${score} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((star) => (
        <Star key={star} size={14} fill={star <= Math.round(score) ? 'currentColor' : 'none'} />
      ))}
    </span>
  )
}

function ProductMark({ product, small = false }: { product: Product; small?: boolean }) {
  return (
    <span
      className={`product-mark ${small ? 'product-mark--small' : ''}`}
      style={{ background: product.logoBackground, color: product.accent }}
      aria-hidden="true"
    >
      {product.initials}
    </span>
  )
}

function goTo(path: string) {
  window.history.pushState({}, '', path)
  window.dispatchEvent(new PopStateEvent('popstate'))
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

function InternalLink({ href, className, children }: { href: string; className?: string; children: React.ReactNode }) {
  return <a href={href} className={className} onClick={(event) => { event.preventDefault(); goTo(href) }}>{children}</a>
}

function ReviewCard({ product, rank }: { product: Product; rank: number }) {
  return (
    <article className="review-card" id={product.slug}>
      <div className="review-rank">{String(rank).padStart(2, '0')}</div>
      <div className="review-main">
        <div className="product-heading">
          <ProductMark product={product} />
          <div>
            <div className="eyebrow-row">
              <span className="category-tag">{product.category}</span>
              {product.editorsChoice && <span className="choice-tag">Editor’s choice</span>}
            </div>
            <h3>{product.name}</h3>
            <p className="tagline">{product.tagline}</p>
          </div>
        </div>
        <p className="review-description">{product.description}</p>
        <div className="best-for"><strong>Best for:</strong> {product.bestFor}</div>
        <InternalLink className="text-link" href={`/reviews/${product.slug}`}>
          Read full review <ArrowRight size={15} />
        </InternalLink>
      </div>
      <aside className="score-panel">
        <div className="score">{product.score}</div>
        <Stars score={product.score} />
        <span className="review-count">{product.reviews.toLocaleString()} user reviews</span>
        <div className="price">{product.pricing}</div>
        <a className="visit-button" href={product.partnerUrl} target="_blank" rel="nofollow sponsored noopener">
          Visit official site <ExternalLink size={16} />
        </a>
      </aside>
    </article>
  )
}

function ProductDetail({ product }: { product: Product }) {
  useEffect(() => {
    const title = `${product.name} Review 2026: Features, Pricing, Pros & Cons | AIGearBase`
    const description = `${product.name} review: an independent look at features, pricing, strengths, limitations, and who should use it. Updated ${product.updated}.`
    document.title = title
    const setMeta = (selector: string, attribute: string, value: string) => {
      let element = document.querySelector(selector) as HTMLMetaElement | HTMLLinkElement | null
      if (!element) { element = document.createElement(selector.startsWith('link') ? 'link' : 'meta'); document.head.appendChild(element) }
      element.setAttribute(attribute, value)
    }
    setMeta('meta[name="description"]', 'content', description)
    setMeta('meta[property="og:title"]', 'content', title)
    setMeta('meta[property="og:description"]', 'content', description)
    setMeta('meta[property="og:url"]', 'content', `https://aigearbase.co/reviews/${product.slug}`)
    setMeta('link[rel="canonical"]', 'href', `https://aigearbase.co/reviews/${product.slug}`)
    const schema = document.createElement('script')
    schema.type = 'application/ld+json'
    schema.id = 'review-schema'
    schema.text = JSON.stringify({ '@context': 'https://schema.org', '@type': 'Review', itemReviewed: { '@type': 'SoftwareApplication', name: product.name, applicationCategory: `${product.category}Application`, url: product.partnerUrl, offers: { '@type': 'Offer', description: product.pricing } }, author: { '@type': 'Organization', name: 'AIGearBase', url: 'https://aigearbase.co/' }, reviewRating: { '@type': 'Rating', ratingValue: product.score, bestRating: 5, worstRating: 1 }, reviewBody: product.verdict, dateModified: product.updated })
    document.head.appendChild(schema)
    return () => schema.remove()
  }, [product])

  return <main className="detail-page">
    <div className="detail-breadcrumb"><InternalLink href="/">Home</InternalLink><span>/</span><InternalLink href="/#reviews">Reviews</InternalLink><span>/</span><span>{product.name}</span></div>
    <section className="detail-hero">
      <div className="detail-title">
        <div className="eyebrow-row"><span className="category-tag">{product.category} software</span><span className="choice-tag">Updated {product.updated}</span></div>
        <h1>{product.name} review: <em>is it right for your team?</em></h1>
        <p>{product.description}</p>
        <div className="byline">Reviewed by the AIGearBase editorial team · 12 min read</div>
      </div>
      <aside className="detail-rating-card">
        <ProductMark product={product} />
        <div><strong>{product.name}</strong><span>{product.tagline}</span></div>
        <div className="detail-score"><b>{product.score}</b><div><Stars score={product.score} /><span>AIGearBase rating</span></div></div>
        <a className="official-button" href={product.partnerUrl} target="_blank" rel="nofollow sponsored noopener">Visit {product.name} website <ExternalLink size={17} /></a>
        <small>We may earn a commission if you buy through our link. This does not affect our rating.</small>
      </aside>
    </section>
    <nav className="detail-nav" aria-label="Review contents"><a href="#verdict">Verdict</a><a href="#features">Features</a><a href="#pros-cons">Pros & cons</a><a href="#pricing">Pricing</a><a href="#alternatives">Alternatives</a></nav>
    <div className="detail-layout">
      <article className="detail-content">
        <section id="verdict"><span className="section-index">01 / Bottom line</span><h2>Our verdict</h2><div className="verdict-box"><b>{product.score}/5</b><p>{product.verdict}</p></div>{product.summary.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}</section>
        <section id="features"><span className="section-index">02 / Product tour</span><h2>Key features</h2><div className="feature-list">{product.keyFeatures.map((feature, index) => <div key={feature.title}><span>{String(index + 1).padStart(2, '0')}</span><div><h3>{feature.title}</h3><p>{feature.description}</p></div></div>)}</div></section>
        <section id="pros-cons"><span className="section-index">03 / Trade-offs</span><h2>{product.name} pros and cons</h2><div className="pros-cons"><div><h3>What we like</h3>{product.pros.map(item => <p key={item}><Check size={16}/>{item}</p>)}</div><div className="cons"><h3>What to consider</h3>{product.cons.map(item => <p key={item}><X size={16}/>{item}</p>)}</div></div></section>
        <section id="pricing"><span className="section-index">04 / Cost</span><h2>Pricing and value</h2><p><strong>Plan availability:</strong> {product.pricing}.</p><p>Pricing and included usage can change. Confirm current plan limits, taxes, contract terms, and usage-based charges on the official website before purchasing. Choose a plan based on the workflow you need today rather than the longest feature list.</p><a className="inline-official" href={product.partnerUrl} target="_blank" rel="nofollow sponsored noopener">Check current pricing on {product.name} <ExternalLink size={16}/></a></section>
        <section id="alternatives"><span className="section-index">05 / Compare</span><h2>Alternatives to consider</h2><div className="alternative-list">{product.alternatives.map(name => <span key={name}>{name}<ArrowRight size={15}/></span>)}</div><div className="not-for"><strong>Who should look elsewhere?</strong><p>{product.notFor}</p></div></section>
        <section className="review-method-note"><h3>How we evaluate {product.name}</h3><p>Our assessment considers product capability, usability, integrations, support resources, pricing transparency, and fit for the stated audience. Scores are editorial judgments, not user-review averages. Product details are checked against available official information, but buyers should verify current terms directly.</p></section>
      </article>
      <aside className="sticky-cta"><span className="section-index">Quick decision</span><h3>Best for</h3><p>{product.bestFor}</p><a className="official-button" href={product.partnerUrl} target="_blank" rel="nofollow sponsored noopener">Visit official website <ExternalLink size={16}/></a><InternalLink className="back-link" href="/">← Back to all reviews</InternalLink></aside>
    </div>
  </main>
}

function CatalogMark({ product }: { product: CatalogProduct }) {
  return <span className="catalog-mark" style={{ background: product.color }}>{product.name.charAt(0).toUpperCase()}</span>
}

function SoftwareDirectory() {
  const [query, setQuery] = useState('')
  const [category, setCategory] = useState('All')
  const [page, setPage] = useState(1)
  const pageSize = 24
  const availableCategories = useMemo(() => ['All', ...Array.from(new Set(catalog.flatMap(item => item.categories))).sort()], [])
  const results = useMemo(() => catalog.filter(item => {
    const text = `${item.name} ${item.description} ${item.categories.join(' ')}`.toLowerCase()
    return text.includes(query.toLowerCase()) && (category === 'All' || item.categories.includes(category))
  }).sort((a, b) => b.popularity - a.popularity), [query, category])
  const pages = Math.max(1, Math.ceil(results.length / pageSize))
  const visible = results.slice((page - 1) * pageSize, page * pageSize)
  useEffect(() => setPage(1), [query, category])
  useEffect(() => { document.title = 'SaaS Software Directory: 400+ Tools Reviewed | AIGearBase' }, [])
  return <main className="directory-page">
    <section className="directory-hero"><span className="section-index">Independent software directory</span><h1>Explore tools for<br/><em>better work.</em></h1><p>Discover {catalog.length} software products from the PartnerStack ecosystem. Search by need, compare positioning, and open an editorial profile before visiting the vendor.</p><label className="directory-search"><Search size={19}/><input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search products, use cases, or categories…"/><span>{results.length} results</span></label></section>
    <section className="directory-body">
      <aside className="filter-panel"><span className="section-index">Filter by category</span>{availableCategories.slice(0, 18).map(item => <button className={category === item ? 'active' : ''} onClick={() => setCategory(item)} key={item}>{item}<span>{item === 'All' ? catalog.length : catalog.filter(p => p.categories.includes(item)).length}</span></button>)}</aside>
      <div className="catalog-results"><div className="catalog-toolbar"><div><b>{results.length} products</b><span>{category === 'All' ? 'All software categories' : category}</span></div><span>Sorted by ecosystem popularity</span></div><div className="catalog-grid">{visible.map(product => <InternalLink href={`/software/${product.slug}`} className="catalog-card" key={product.slug}><div className="catalog-card-top"><CatalogMark product={product}/>{product.promotions.length > 0 && <span className="promotion-tag">Offer available</span>}</div><h2>{product.name}</h2><p>{product.description}</p><div className="catalog-tags">{product.categories.slice(0,2).map(tag => <span key={tag}>{tag}</span>)}</div><div className="catalog-card-link">View product profile <ArrowRight size={16}/></div></InternalLink>)}</div>{visible.length === 0 && <div className="empty-state"><Search/><h3>No matching software</h3><p>Try a shorter query or choose another category.</p></div>}<div className="pagination"><button disabled={page === 1} onClick={() => {setPage(page - 1);window.scrollTo(0,350)}}>Previous</button><span>Page {page} of {pages}</span><button disabled={page === pages} onClick={() => {setPage(page + 1);window.scrollTo(0,350)}}>Next</button></div></div>
    </section>
  </main>
}

function CatalogDetail({ product }: { product: CatalogProduct }) {
  const flagship = products.find(item => item.name.toLowerCase().includes(product.name.split(' ')[0].toLowerCase()) || product.slug.startsWith(item.slug.split('-')[0]))
  const related = catalog.filter(item => item.slug !== product.slug && item.categories.some(category => product.categories.includes(category))).slice(0, 3)
  useEffect(() => {
    const title = `${product.name} Review, Features & Alternatives | AIGearBase`
    const description = `${product.name} overview: what it does, key use cases, categories, alternatives, and a direct link to the official website.`
    document.title = title
    document.querySelector('meta[name="description"]')?.setAttribute('content', description)
    document.querySelector('link[rel="canonical"]')?.setAttribute('href', `https://aigearbase.co/software/${product.slug}`)
  }, [product])
  if (flagship) return <ProductDetail product={flagship}/>
  return <main className="profile-page">
    <div className="detail-breadcrumb"><InternalLink href="/">Home</InternalLink><span>/</span><InternalLink href="/software">Software</InternalLink><span>/</span><span>{product.name}</span></div>
    <section className="profile-hero"><div><div className="profile-heading"><CatalogMark product={product}/><span>{product.categories[0] || 'Business software'}</span></div><h1>{product.name}: product overview and review</h1><p>{product.description}</p><div className="profile-actions"><a className="official-button" href={product.website} target="_blank" rel="nofollow sponsored noopener">Visit official website <ExternalLink size={17}/></a><InternalLink className="outline-profile-button" href="/software">Browse all software</InternalLink></div><small className="affiliate-note">Affiliate disclosure: we may earn a commission from qualifying referrals. Our editorial descriptions are not sold to vendors.</small></div><aside className="profile-facts"><span className="section-index">At a glance</span><dl><div><dt>Product</dt><dd>{product.name}</dd></div><div><dt>Category</dt><dd>{product.categories[0] || 'Business software'}</dd></div><div><dt>Promotions</dt><dd>{product.promotions.length ? product.promotions.join(', ').replaceAll('_',' ') : 'Check vendor website'}</dd></div><div><dt>Review status</dt><dd>Editorial profile</dd></div></dl></aside></section>
    <section className="profile-content"><article><section><span className="section-index">01 / Overview</span><h2>What is {product.name}?</h2><p>{product.description}</p><p>{product.name} is listed in the PartnerStack software ecosystem under {product.categories.length ? product.categories.join(', ') : 'business software'}. This profile helps you understand its positioning and decide whether it deserves a place on your shortlist.</p><div className="accuracy-note"><Sparkles size={20}/><div><strong>Our accuracy standard</strong><p>This page does not assign a score without a completed hands-on editorial review. Product availability and capabilities can change; verify requirements with the vendor before buying.</p></div></div></section><section><span className="section-index">02 / Evaluation</span><h2>Questions to ask before choosing {product.name}</h2><div className="question-grid"><div><b>Workflow fit</b><p>Does it support your most frequent workflow without extensive customization?</p></div><div><b>Adoption</b><p>Can your team learn the product, and are onboarding resources sufficient?</p></div><div><b>Integration</b><p>Does it connect cleanly to your existing source-of-truth systems?</p></div><div><b>Total cost</b><p>Which usage limits, add-ons, implementation costs, or contracts apply?</p></div></div></section><section><span className="section-index">03 / Next step</span><h2>How to evaluate it</h2><ol className="evaluation-steps"><li><span>01</span><p>Write down one concrete workflow and the result you expect.</p></li><li><span>02</span><p>Ask the vendor to demonstrate that workflow using realistic data.</p></li><li><span>03</span><p>Run a small pilot with the people who will use the software.</p></li><li><span>04</span><p>Confirm current terms and ownership of exported data before committing.</p></li></ol><a className="inline-official" href={product.website} target="_blank" rel="nofollow sponsored noopener">Explore {product.name} on its official site <ExternalLink size={16}/></a></section></article><aside><div className="related-box"><span className="section-index">Similar products</span>{related.map(item => <InternalLink href={`/software/${item.slug}`} key={item.slug}><CatalogMark product={item}/><span><b>{item.name}</b><small>{item.categories[0] || 'Software'}</small></span><ArrowRight size={15}/></InternalLink>)}</div></aside></section>
  </main>
}

function App() {
  const [search, setSearch] = useState('')
  const [mobileOpen, setMobileOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [subscribed, setSubscribed] = useState(false)
  const [path, setPath] = useState(window.location.pathname)
  useEffect(() => { const update = () => setPath(window.location.pathname); window.addEventListener('popstate', update); return () => window.removeEventListener('popstate', update) }, [])
  const detailProduct = products.find(product => path === `/reviews/${product.slug}`)
  const catalogProduct = catalog.find(product => path === `/software/${product.slug}`)
  const isDirectory = path === '/software' || path === '/software/'
  useEffect(() => {
    if (path === '/') {
      document.title = 'AIGearBase — Independent SaaS Reviews, Comparisons & Deals'
      document.querySelector('meta[name="description"]')?.setAttribute('content', 'Make smarter software decisions with in-depth SaaS reviews, transparent comparisons, and expert buying guides from AIGearBase.')
      document.querySelector('link[rel="canonical"]')?.setAttribute('href', 'https://aigearbase.co/')
    }
  }, [path])

  const filteredProducts = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return products.slice(0, 5)
    return products.filter((product) =>
      [product.name, product.category, product.tagline, product.bestFor].some((value) => value.toLowerCase().includes(term)),
    )
  }, [search])

  const submitNewsletter = (event: React.FormEvent) => {
    event.preventDefault()
    if (email.trim()) setSubscribed(true)
  }

  return (
    <div className="site-shell">
      <header className="site-header">
        <InternalLink className="brand" href="/">
          <span className="brand-glyph"><span /> <span /> <span /></span>
          <span>AIGearBase</span>
        </InternalLink>
        <nav className={mobileOpen ? 'nav nav--open' : 'nav'} aria-label="Main navigation">
          <InternalLink href="/software">Software directory</InternalLink>
          <a href="/#reviews">Reviews</a>
          <a href="/#categories">Categories</a>
          <a href="/#guides">Buying guides</a>
          <a href="/#methodology">How we review</a>
        </nav>
        <div className="header-actions">
          <a className="about-link" href="#about">About</a>
          <button className="menu-button" onClick={() => setMobileOpen(!mobileOpen)} aria-label="Toggle menu">
            {mobileOpen ? <X /> : <Menu />}
          </button>
        </div>
      </header>

      {detailProduct ? <ProductDetail product={detailProduct} /> : catalogProduct ? <CatalogDetail product={catalogProduct}/> : isDirectory ? <SoftwareDirectory/> : <main id="top">
        <section className="hero">
          <div className="hero-orbit orbit-one" />
          <div className="hero-orbit orbit-two" />
          <div className="hero-content">
            <div className="overline"><span /> Independent software research</div>
            <h1>Find software that<br /><em>moves work forward.</em></h1>
            <p>Clear, independent reviews for people who would rather make a good decision than read another list of features.</p>
            <label className="search-box">
              <Search size={20} />
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search 200+ software reviews"
                aria-label="Search software reviews"
              />
              <span>⌘ K</span>
            </label>
            <div className="hero-notes">
              <span><Check size={14} /> Hands-on testing</span>
              <span><Check size={14} /> No pay-to-rank</span>
              <span><Check size={14} /> Updated monthly</span>
            </div>
          </div>
          <aside className="hero-score-card">
            <span className="tiny-label">This month’s top pick</span>
            <ProductMark product={products[0]} />
            <div>
              <strong>Apollo.io</strong>
              <span>Sales intelligence</span>
            </div>
            <div className="mini-score"><b>4.8</b><Stars score={4.8} /></div>
            <InternalLink href="/reviews/apollo-io-review">Why it won <ArrowRight size={14} /></InternalLink>
          </aside>
        </section>

        <section className="trust-strip" aria-label="AIGearBase principles">
          <span>Research-led</span><i />
          <span>Editorially independent</span><i />
          <span>Built for real decisions</span><i />
          <span>200+ products tested</span>
        </section>

        <section className="section categories-section" id="categories">
          <div className="section-heading split-heading">
            <div>
              <span className="section-index">01 / Explore</span>
              <h2>Browse by category</h2>
            </div>
            <p>Start with the kind of work you need to improve. We’ll help narrow down the tools worth your time.</p>
          </div>
          <div className="category-grid">
            {categories.map((category) => {
              const Icon = iconMap[category.icon as keyof typeof iconMap]
              return (
                <a href="#reviews" className="category-card" key={category.name} onClick={() => setSearch(category.name.split(' ')[0])}>
                  <span className="category-icon"><Icon size={22} /></span>
                  <strong>{category.name}</strong>
                  <span>{category.count} reviews</span>
                  <ArrowRight className="category-arrow" size={18} />
                </a>
              )
            })}
          </div>
        </section>

        <section className="reviews-section" id="reviews">
          <div className="section reviews-inner">
            <div className="section-heading reviews-heading">
              <div>
                <span className="section-index">02 / Top rated</span>
                <h2>{search ? `Results for “${search}”` : 'Software worth a closer look'}</h2>
              </div>
              {search && <button className="clear-search" onClick={() => setSearch('')}>Clear search <X size={14} /></button>}
            </div>
            <div className="review-list">
              {filteredProducts.length ? filteredProducts.map((product, index) => (
                <ReviewCard key={product.slug} product={product} rank={index + 1} />
              )) : (
                <div className="empty-state"><Search /><h3>No reviews found</h3><p>Try a product name or a broader category.</p></div>
              )}
            </div>
            <InternalLink className="outline-button" href="/software">Explore all {catalog.length} products <ArrowRight size={16} /></InternalLink>
          </div>
        </section>

        <section className="section methodology" id="methodology">
          <div className="method-copy">
            <span className="section-index">03 / Our standard</span>
            <h2>Recommendations you can actually <em>trust.</em></h2>
            <p>Software reviews have a credibility problem. We built AIGearBase around a straightforward idea: test the product, explain the trade-offs, and keep commercial relationships out of the score.</p>
            <a className="text-link" href="#guides">See our review methodology <ArrowRight size={15} /></a>
          </div>
          <div className="method-grid">
            <div><span>01</span><h3>We use the product</h3><p>Every major review starts with hands-on testing against real workflows—not rewritten marketing copy.</p></div>
            <div><span>02</span><h3>We show our work</h3><p>Scores reflect consistent criteria including capability, usability, support, value, and long-term fit.</p></div>
            <div><span>03</span><h3>We stay independent</h3><p>Partners may pay us a commission, but they cannot buy rankings, scores, or editorial approval.</p></div>
            <div><span>04</span><h3>We keep it current</h3><p>Pricing and product changes are checked monthly. Full reviews receive scheduled editorial updates.</p></div>
          </div>
        </section>

        <section className="editorial" id="guides">
          <div className="section editorial-inner">
            <div className="section-heading split-heading">
              <div><span className="section-index">04 / From the desk</span><h2>Guides for better buying</h2></div>
              <a className="text-link" href="#all-articles">View all articles <ArrowRight size={15} /></a>
            </div>
            <div className="article-grid">
              {articles.map((article, index) => (
                <article className="article-card" key={article.title}>
                  <div className="article-visual" style={{ background: article.accent }}>
                    <span>{String(index + 1).padStart(2, '0')}</span>
                    <div className={`abstract abstract-${index + 1}`}><i /><i /><i /></div>
                  </div>
                  <div className="article-meta"><span>{article.category}</span><span>{article.readTime}</span></div>
                  <h3>{article.title}</h3>
                  <p>{article.excerpt}</p>
                  <div className="article-footer"><span>{article.date}</span><ArrowRight size={18} /></div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="newsletter" id="about">
          <div>
            <span className="section-index light">The useful email</span>
            <h2>One smart software decision,<br /><em>every other Tuesday.</em></h2>
          </div>
          {subscribed ? (
            <div className="success-message"><Check /> You’re on the list. Watch your inbox.</div>
          ) : (
            <form onSubmit={submitNewsletter}>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" aria-label="Work email" />
              <button>Join 8,000+ readers <ArrowRight size={16} /></button>
              <small>No noise. Unsubscribe whenever you like.</small>
            </form>
          )}
        </section>
      </main>}

      <footer className="footer">
        <div className="footer-top">
          <div className="footer-brand"><a className="brand" href="#top"><span className="brand-glyph"><span /><span /><span /></span>AIGearBase</a><p>Independent software research for modern teams.</p></div>
          <div><strong>Discover</strong><InternalLink href="/software">Software directory</InternalLink><a href="/#reviews">Top reviews</a><a href="/#categories">Categories</a><a href="/#guides">Buying guides</a></div>
          <div><strong>Company</strong><a href="#about">About us</a><a href="#methodology">How we review</a><a href="mailto:hello@aigearbase.co">Contact</a></div>
          <div><strong>Legal</strong><a href="#privacy">Privacy</a><a href="#terms">Terms</a><a href="#disclosure">Affiliate disclosure</a></div>
        </div>
        <div className="footer-bottom"><span>© 2026 AIGearBase. All rights reserved.</span><span>Made for better decisions.</span></div>
      </footer>
    </div>
  )
}

export default App
